import React, { useEffect, useRef } from 'react'
import { formatTaxPercent, validateTaxPercent } from '../../../utils/shippingTax'

export type ZoneTaxMode = 'default' | 'custom'

interface Props {
  mode: ZoneTaxMode
  value: string
  /** Location-level shipping tax the zone inherits when mode is "default". */
  defaultTax: string
  regionName: string
  onModeChange: (mode: ZoneTaxMode) => void
  onValueChange: (value: string) => void
  disabled?: boolean
  /** Focus the custom-rate input on mount (deep link from Overview). */
  autoFocus?: boolean
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 500,
  color: '#5A6B7B',
  marginBottom: 6,
}

const optionBaseStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  padding: '10px 12px',
  border: '1px solid #E8ECF0',
  borderRadius: 8,
  cursor: 'pointer',
  background: '#fff',
  flex: 1,
  minWidth: 0,
}

const optionActiveStyle: React.CSSProperties = {
  ...optionBaseStyle,
  borderColor: '#2563EB',
  background: '#EFF6FF',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '8px 32px 8px 12px',
  fontSize: 13,
  color: '#2C3E50',
  background: '#fff',
  border: '1px solid #E8ECF0',
  borderRadius: 8,
  outline: 'none',
  fontFamily: 'inherit',
  MozAppearance: 'textfield',
}

const unitStyle: React.CSSProperties = {
  position: 'absolute',
  right: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  fontSize: 13,
  color: '#B0BEC5',
  pointerEvents: 'none',
}

const helperStyle: React.CSSProperties = {
  margin: '6px 0 0',
  fontSize: 12,
  color: '#7F8C8D',
}

export const ZoneShippingTaxField: React.FC<Props> = ({
  mode,
  value,
  defaultTax,
  regionName,
  onModeChange,
  onValueChange,
  disabled = false,
  autoFocus = false,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const defaultLabel = formatTaxPercent(defaultTax)
  const isCustom = mode === 'custom'
  const error = isCustom ? validateTaxPercent(value) : null
  const sameAsDefault =
    isCustom && !error && Number(value) === Number(defaultTax || 0)

  useEffect(() => {
    if (autoFocus && isCustom) inputRef.current?.focus()
  }, [autoFocus, isCustom])

  const renderOption = (
    optionMode: ZoneTaxMode,
    title: string,
    subtitle: string,
  ) => {
    const active = mode === optionMode
    return (
      <label style={active ? optionActiveStyle : optionBaseStyle}>
        <input
          type="radio"
          name="zone-tax-mode"
          checked={active}
          onChange={() => onModeChange(optionMode)}
          disabled={disabled}
          style={{ marginTop: 2, accentColor: '#2563EB' }}
        />
        <span style={{ minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#2C3E50' }}>
            {title}
          </span>
          <span style={{ display: 'block', fontSize: 11.5, color: '#7F8C8D', marginTop: 2 }}>
            {subtitle}
          </span>
        </span>
      </label>
    )
  }

  return (
    <div>
      <span style={labelStyle}>Shipping Tax</span>
      <div role="radiogroup" aria-label="Shipping tax" style={{ display: 'flex', gap: 10 }}>
        {renderOption(
          'default',
          `Use location default (${defaultLabel})`,
          'Follows the location shipping tax',
        )}
        {renderOption('custom', 'Custom rate', 'Override for this zone only')}
      </div>

      {isCustom ? (
        <div style={{ marginTop: 10 }}>
          <div style={{ position: 'relative', maxWidth: 180 }}>
            <input
              ref={inputRef}
              id="zone-shipping-tax"
              type="number"
              inputMode="decimal"
              min={0}
              max={100}
              step={0.01}
              value={value}
              onChange={(e) => onValueChange(e.target.value)}
              placeholder={String(Number(defaultTax || 0))}
              aria-label="Custom shipping tax percentage"
              aria-invalid={!!error}
              aria-describedby="zone-shipping-tax-help"
              style={{ ...inputStyle, borderColor: error ? '#FCA5A5' : '#E8ECF0' }}
              disabled={disabled}
            />
            <span style={unitStyle}>%</span>
          </div>
          {error ? (
            <p id="zone-shipping-tax-help" role="alert" style={{ ...helperStyle, color: '#DC2626' }}>
              {error}
            </p>
          ) : (
            <p id="zone-shipping-tax-help" style={helperStyle}>
              Overrides the location shipping tax ({defaultLabel}) for orders shipped to{' '}
              {regionName || 'this zone'}.
              {sameAsDefault && ' Same as location default.'}
            </p>
          )}
        </div>
      ) : (
        <p style={helperStyle}>
          Orders shipped to {regionName || 'this zone'} are taxed at the location rate (
          {defaultLabel}). It updates automatically if the location rate changes.
        </p>
      )}
    </div>
  )
}
